import {
  getAllAssetIdsInvolvedInStrategy,
  safeParseStrategyDocument,
} from "@repo/strategy";
import { parsePortfolioAssetId } from "~/features/portfolio-domain";
import { readString, toRecord } from "./portfolio-parsers";

export interface StarterTokenMapRow {
  assetId: string;
  token?: {
    info?: {
      imageLargeUrl?: string;
      imageSmallUrl?: string;
      imageThumbUrl?: string;
    };
    name?: string;
    symbol?: string;
  };
}

function normalizeStarterTokenRow(input: {
  assetId: string;
  rowRecord: Record<string, unknown>;
}): StarterTokenMapRow {
  const tokenRecord = toRecord(input.rowRecord.token);
  const tokenInfoRecord = toRecord(tokenRecord?.info);

  return {
    assetId: input.assetId,
    token: tokenRecord
      ? {
          info: tokenInfoRecord
            ? {
                imageLargeUrl: readString(tokenInfoRecord.imageLargeUrl),
                imageSmallUrl: readString(tokenInfoRecord.imageSmallUrl),
                imageThumbUrl: readString(tokenInfoRecord.imageThumbUrl),
              }
            : undefined,
          name: readString(tokenRecord.name),
          symbol: readString(tokenRecord.symbol),
        }
      : undefined,
  };
}

export function normalizeTokenMap(value: unknown): Record<string, StarterTokenMapRow> {
  const asMap = toRecord(value);
  if (asMap) {
    const rows: Record<string, StarterTokenMapRow> = {};
    for (const [key, row] of Object.entries(asMap)) {
      const rowRecord = toRecord(row);
      if (rowRecord) {
        const assetId = readString(rowRecord.assetId) ?? key;
        rows[assetId] = normalizeStarterTokenRow({
          assetId,
          rowRecord,
        });
      }
    }
    return rows;
  }

  if (!Array.isArray(value)) {
    return {};
  }

  const rows: Record<string, StarterTokenMapRow> = {};
  for (const item of value) {
    const itemRecord = toRecord(item);
    const assetId = readString(itemRecord?.assetId);
    if (!assetId || !itemRecord) {
      continue;
    }

    rows[assetId] = normalizeStarterTokenRow({
      assetId,
      rowRecord: itemRecord,
    });
  }

  return rows;
}

export function deriveTemplateAssetIds(
  strategyData: unknown,
): string[] {
  const parsed = safeParseStrategyDocument(strategyData);
  if (!parsed.success) {
    return [];
  }

  try {
    return getAllAssetIdsInvolvedInStrategy(parsed.data.entry).assets.map(
      (asset) => asset.assetId,
    );
  } catch {
    return [];
  }
}

export function deriveTemplateChainId(
  strategyData: unknown,
): number {
  const assetIds = deriveTemplateAssetIds(strategyData);
  for (const assetId of assetIds) {
    const parsed = parsePortfolioAssetId(assetId);
    if (!parsed) {
      continue;
    }

    const chain = Number.parseInt(parsed.chainRef, 10);
    if (Number.isFinite(chain)) {
      return chain;
    }
  }

  return 8453;
}
